import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  WORKSPACE_PRESENCE_HEARTBEAT_MS,
  WORKSPACE_PRESENCE_POLL_MS,
} from "@/lib/workspacePresence";

function isDocumentVisible() {
  if (typeof document === "undefined") return true;
  return document.visibilityState !== "hidden";
}

/**
 * Heartbeat global: mantiene last_seen_at mientras hay sesión y pestaña visible.
 */
export function useWorkspacePresenceHeartbeat(enabled = true) {
  const { user } = useAuth();
  const timerRef = useRef(null);

  const ping = useCallback(async () => {
    if (!user || !enabled || !isDocumentVisible()) return;
    try {
      await api.post("/workspace/chat/presence", null, { timeout: 15000 });
    } catch {
      /* ignore transient network errors */
    }
  }, [user, enabled]);

  useEffect(() => {
    if (!user || !enabled) return undefined;
    ping();
    timerRef.current = window.setInterval(ping, WORKSPACE_PRESENCE_HEARTBEAT_MS);
    const onVisible = () => {
      if (isDocumentVisible()) ping();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, enabled, ping]);
}

/**
 * Lista de presencia del workspace para «En línea ahora» en el chat interno.
 */
export function useWorkspacePresenceList({ enabled = true, pollMs = WORKSPACE_PRESENCE_POLL_MS } = {}) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [onlineWindowMinutes, setOnlineWindowMinutes] = useState(5);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user || !enabled) {
      setItems([]);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/workspace/chat/presence", { timeout: 15000 });
      setItems(Array.isArray(data?.items) ? data.items : []);
      if (data?.online_window_minutes != null) {
        setOnlineWindowMinutes(Number(data.online_window_minutes) || 5);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user, enabled]);

  useEffect(() => {
    if (!user || !enabled) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    refresh();
    timerRef.current = window.setInterval(refresh, pollMs);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [user, enabled, pollMs, refresh]);

  return { items, onlineWindowMinutes, loading, refresh };
}
